/**
 * @fileoverview HTTP API client for CRUD operations with session authentication,
 * sign-out on 401, and type-safe responses.
 */

import type { CurrentUser as User } from './session';
import { handleUnauthorizedResponse, sessionAuth } from './session';
import { API_BASE_URL } from '@/config/app';
import { ApiResponse } from './types';
import { TIMEOUT_MS } from './constants';

/**
 * Request parameters for paginated list queries.
 */
export interface RequestParams {
  page: string;
  pageSize: string;
  sortField?: string;
  sortOrder?: string;
  search?: string;
}

/**
 * Standard API error response structure.
 */
export interface ApiErrorResponse {
  error?: string;
  message?: string;
  code?: string;
  details?: Record<string, string[]>;
}

/**
 * Custom error class for API errors with additional context.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly code?: string;
  readonly details?: Record<string, string[]>;

  constructor(
    message: string,
    status: number,
    statusText: string,
    code?: string,
    details?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.code = code;
    this.details = details;
  }

  /**
   * Checks if this is a specific HTTP status error.
   */
  isStatus(status: number): boolean {
    return this.status === status;
  }

  /**
   * Checks if this is a client error (4xx).
   */
  isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /**
   * Checks if this is a server error (5xx).
   */
  isServerError(): boolean {
    return this.status >= 500;
  }
}

/**
 * Type guard to check if an error is an ApiError.
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * HTTP API client for CRUD operations with session authentication.
 */
export class ApiClient {
  private readonly baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get session auth token for authenticated requests.
   */
  private async getAuthToken(): Promise<string | null> {
    let user = sessionAuth.currentUser;
    if (!user) {
      user = await this.waitForAuthInit();
    }

    if (!user) {
      return null;
    }

    try {
      return await user.getIdToken();
    } catch {
      return null;
    }
  }

  /**
   * Wait for session auth to initialize and return the current user (or null after timeout).
   */
  private waitForAuthInit(timeoutMs: number = TIMEOUT_MS.AUTH_INIT): Promise<User | null> {
    return new Promise<User | null>((resolve) => {
      const existing = sessionAuth.currentUser;
      if (existing) {
        resolve(existing);
        return;
      }

      let resolved = false;
      let callbackCount = 0;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let unsubscribe = (): void => {};

      const finalize = (user: User | null): void => {
        if (resolved) {
          return;
        }
        resolved = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        try {
          unsubscribe();
        } catch {
          // ignore unsubscribe errors
        }
        resolve(user);
      };

      unsubscribe = sessionAuth.onAuthStateChanged((user) => {
        callbackCount++;

        if (user) {
          // User is authenticated
          finalize(user);
        } else if (callbackCount > 1) {
          // Second or later callback with null means auth has definitively
          // determined there's no session (not just initializing)
          finalize(null);
        }
        // First callback with null: the session might still be restoring,
        // so we keep listening for the next callback
      });

      // Fallback in case auth state never fires quickly in dev
      timeoutId = setTimeout(() => {
        if (!resolved) {
          finalize(sessionAuth.currentUser);
        }
      }, timeoutMs);
    });
  }

  /**
   * Get headers with authentication token.
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await this.getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Fetch helper that attaches auth headers. A 401 means the session token is
   * missing, invalid or expired (there is no refresh token to retry with), so
   * the session is cleared and the app redirects to the sign-in page.
   */
  private async fetchWithAuth(url: string, init?: RequestInit): Promise<Response> {
    const baseHeaders = await this.getAuthHeaders();
    const mergedHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      ...(init?.headers as HeadersInit | undefined),
      ...baseHeaders,
    };

    const response = await fetch(url, { ...init, headers: mergedHeaders });
    handleUnauthorizedResponse(response);
    return response;
  }

  /**
   * Parse error response body safely.
   */
  private async parseErrorResponse(response: Response): Promise<ApiErrorResponse | null> {
    try {
      const data: unknown = await response.json();
      // Type guard for error response structure
      if (typeof data === 'object' && data !== null) {
        const errorData = data as Record<string, unknown>;
        return {
          error: typeof errorData.error === 'string' ? errorData.error : undefined,
          message: typeof errorData.message === 'string' ? errorData.message : undefined,
          code: typeof errorData.code === 'string' ? errorData.code : undefined,
          details: this.isDetailsObject(errorData.details) ? errorData.details : undefined,
        };
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Type guard for error details object.
   */
  private isDetailsObject(value: unknown): value is Record<string, string[]> {
    if (typeof value !== 'object' || value === null) {
      return false;
    }
    return Object.values(value).every(
      (v) => Array.isArray(v) && v.every((item) => typeof item === 'string')
    );
  }

  /**
   * Extract a descriptive error message from an API error response.
   */
  private extractErrorMessage(errorData: ApiErrorResponse | null, response: Response): string {
    const statusDescription = `${response.status} ${response.statusText}`;

    if (errorData?.error && errorData?.message) {
      return `${errorData.error}: ${errorData.message}`;
    }

    if (errorData?.error) {
      return errorData.error;
    }

    if (errorData?.message) {
      return errorData.message;
    }

    return statusDescription;
  }

  /**
   * Handle API response and throw typed errors for non-OK responses.
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await this.parseErrorResponse(response);

      if (response.status === 404) {
        const resource = response.url.split('/').pop() ?? 'Resource';
        throw new ApiError(
          errorData?.error ?? errorData?.message ?? `${resource} not found`,
          response.status,
          response.statusText,
          errorData?.code,
          errorData?.details
        );
      }

      throw new ApiError(
        this.extractErrorMessage(errorData, response),
        response.status,
        response.statusText,
        errorData?.code,
        errorData?.details
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }

  /**
   * Fetch paginated list of items.
   */
  async getMany<T>(endpoint: string, params: RequestParams): Promise<ApiResponse<T>> {
    const urlParams = new URLSearchParams();
    urlParams.append('page', params.page);
    urlParams.append('pageSize', params.pageSize);

    if (params.sortField) {
      urlParams.append('sortField', params.sortField);
      urlParams.append('sortOrder', params.sortOrder ?? 'asc');
    }

    if (params.search) {
      urlParams.append('search', params.search);
    }

    const response = await this.fetchWithAuth(`${this.baseUrl}/${endpoint}?${urlParams}`);
    return this.handleResponse<ApiResponse<T>>(response);
  }

  /**
   * Fetch a single item by ID.
   */
  async getOne<T>(endpoint: string, id: string): Promise<T> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/${endpoint}/${id}`);
    return this.handleResponse<T>(response);
  }

  /**
   * Create a new item.
   */
  async createOne<T, TInput = Partial<T>>(endpoint: string, data: TInput): Promise<T> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.handleResponse<T>(response);
  }

  /**
   * Generic GET request for custom endpoints (e.g. sub-resources).
   */
  async get<T>(endpoint: string): Promise<T> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/${endpoint}`);
    return this.handleResponse<T>(response);
  }

  /**
   * Generic POST request.
   */
  async post<T, TInput = unknown>(endpoint: string, data?: TInput): Promise<T> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/${endpoint}`, {
      method: 'POST',
      body: data === undefined ? undefined : JSON.stringify(data),
    });
    return this.handleResponse<T>(response);
  }

  /**
   * Update an existing item.
   */
  async updateOne<T, TInput = Partial<T>>(endpoint: string, id: string, data: TInput): Promise<T> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/${endpoint}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return this.handleResponse<T>(response);
  }

  /**
   * Delete an item by ID.
   */
  async deleteOne(endpoint: string, id: string): Promise<void> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/${endpoint}/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorData = await this.parseErrorResponse(response);

      if (response.status === 404) {
        throw new ApiError(
          errorData?.error ?? errorData?.message ?? `${endpoint} not found`,
          response.status,
          response.statusText,
          errorData?.code,
          errorData?.details
        );
      }

      throw new ApiError(
        this.extractErrorMessage(errorData, response),
        response.status,
        response.statusText,
        errorData?.code,
        errorData?.details
      );
    }
  }
}

/** Singleton API client instance */
export const apiClient = new ApiClient();
